/* global jsCalendar */
import {changeOrderPlaceDate, changeSubDeliveryDate} from '../subscription-modal/ordergroove-api-helper';

export default function datePicker() {
    // Ensure the date is formatted as YYYY-MM-DD for input[type="date"]
    function toDateInputValue(d) {
        if (!d) return '';
        const dateObj = new Date(d);
        if (isNaN(dateObj)) return '';
        return dateObj.toISOString().split('T')[0];
    }
    return {
        selectedDate: null,
        minDate: new Date().toISOString().split('T')[0],
        calendar: null,
        type: null,
        orderId: null,
        subscriptionId: null,
        calendarRetries: 0, // retry counter
        
        initCalendar() {
            this.$nextTick(() => {
                if (!this.$refs.calendar || !jsCalendar) {
                    if (typeof this.calendarRetries === 'undefined') this.calendarRetries = 0;
                    if (this.calendarRetries < 10) {
                        this.calendarRetries++;
                        setTimeout(() => { this.initCalendar(); }, 50);
                    } else {
                        throw new Error('Calendar ref not found after multiple attempts or jsCalendar is not loaded');
                    }
                    return;
                }
                this.calendarRetries = 0; // reset on success
                // Defensive: ensure initial date is in YYYY-MM-DD format or a Date object
                let calendarInitDate = this.selectedDate;
                
                // Handle empty, null, or undefined values
                if (!calendarInitDate) {
                    calendarInitDate = new Date().toISOString().split('T')[0];
                } else if (!/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(calendarInitDate)) {
                    const d = new Date(calendarInitDate);
                    if (!isNaN(d)) {
                        calendarInitDate = d.toISOString().split('T')[0];
                    } else {
                        calendarInitDate = new Date().toISOString().split('T')[0];
                    }
                }
                
                try {
                    // Convert string date to Date object, avoiding timezone issues
                    const [year, month, day] = calendarInitDate.split('-').map(Number);
                    const dateObj = new Date(year, month - 1, day); // month is 0-indexed
                    
                    // Set min date to today (users can only select future dates)
                    const today = new Date();
                    const todayMinDateObj = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                    
                    // Clear any existing content
                    this.$refs.calendar.innerHTML = '';
                    
                    this.calendar = jsCalendar.new(this.$refs.calendar, dateObj, {
                        min: todayMinDateObj,
                        navigator : true,
                        navigatorPosition : "both",
                        zeroFill : false,
                        monthFormat : "month YYYY",
                        language : "en"
                    });
                    
                    // Inject custom SVG navigation arrows
                    this.injectJsCalendarNavSVGs(this.$refs.calendar);
                    
                    // Listen for date selection
                    this.calendar.onDateClick((event, dateClicked) => {
                        const yyyy = dateClicked.getFullYear();
                        const mm = String(dateClicked.getMonth() + 1).padStart(2, '0');
                        const dd = String(dateClicked.getDate()).padStart(2, '0');
                        this.selectedDate = `${yyyy}-${mm}-${dd}`;
                        this.calendar.set(dateClicked);
                    });
                } catch (error) {
                    throw new Error('jsCalendar initialization failed with error:', error);
                }
            });
        },
        // Inject custom SVG navigation arrows for jsCalendar
        injectJsCalendarNavSVGs(calendarRoot) {
            // SVG markup from custom-select-arrow (left and right chevrons)
            const leftSVG = `
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" class="custom-select-arrow">
                    <path d="M17 14l-5-5-5 5" stroke="#142978" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            `;
            const rightSVG = `
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" class="custom-select-arrow">
                    <path d="M7 10l5 5 5-5" stroke="#142978" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            `;
            // Find nav elements
            const leftNav = calendarRoot.querySelector('.jsCalendar-nav-left');
            const rightNav = calendarRoot.querySelector('.jsCalendar-nav-right');
            if (leftNav) leftNav.innerHTML = leftSVG;
            if (rightNav) rightNav.innerHTML = rightSVG;
            if (calendarRoot.querySelector('table')) {
                calendarRoot.querySelector('table').classList.add('jsCalendar-table');
            }
        },

      async updateShipDate() {
        if (this.type === 'order') {
          // Track order-level edit date confirmation click
          const newOrderDate = this.selectedDate + ' 00:00:00';
          const locationHash = window.location.hash !== '' ? window.location.hash : '#dashboard';
          const editOrderDateData = window.dsc?.editOrderDateData;
          
          if (editOrderDateData) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'dl_custom_order_edit_date_confirm',
              DSC: {
                location: locationHash,
                ship_date: editOrderDateData.ship_date,
                new_ship_date: newOrderDate,
                ordergroove_public_order_id: editOrderDateData.ordergroove_public_order_id,
                subtotal_amount: editOrderDateData.subtotal_amount,
                total_item_quantity: editOrderDateData.total_item_quantity,
                total_subscription_item_quantity: editOrderDateData.total_subscription_item_quantity,
                items: editOrderDateData.items,
              },
            });
          } else {
            // Do not block the date update if tracking data is missing
            console.warn('editOrderDateData is missing; skipping dataLayer tracking for order date update.');
          }
        }

        try {
            // Format selectedDate as 'YYYY-MM-DD HH:MM:SS' using UTC methods to avoid timezone offset
            const dateObj = new Date(this.selectedDate);
            const now = new Date();
            const pad = n => n.toString().padStart(2, '0');
            const formatted = `${dateObj.getUTCFullYear()}-${pad(dateObj.getUTCMonth()+1)}-${pad(dateObj.getUTCDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
            // Uncomment below to actually call the API
            if (this.subscriptionId != null) {
                // Track subscription-level edit date confirmation click
                const newSubDate = this.selectedDate + ' 00:00:00';
                const editSubDateData = window.dsc?.editSubDateData;

                if (editSubDateData) {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: 'dl_custom_sub_edit_date_confirm',
                    DSC: {
                      location: editSubDateData.location,
                      ship_date: editSubDateData.ship_date,
                      new_ship_date: newSubDate,
                      subscription_id: editSubDateData.subscription_id,
                      item_name: editSubDateData.item_name,
                      item_id: editSubDateData.item_id,
                      item_variant_id: editSubDateData.item_variant_id,
                      quantity: editSubDateData.quantity,
                      sub_frequency: editSubDateData.sub_frequency,
                    },
                  });
                } else {
                  console.warn('editSubDateData is missing; skipping dataLayer tracking for subscription date update.');
                }

                await changeSubDeliveryDate(this.subscriptionId, this.selectedDate).then(async () => {
                    this.$dispatch('toast', { message: 'Date Updating...', type: 'success' });
                    this.$dispatch('update-sub-date', {subscriptionId: this.subscriptionId, date: this.selectedDate})
                    this.$dispatch('close-modal');
                }).catch((error) => {
                    throw new Error('Failed to update order:', error)
                });
            } else {
                await changeOrderPlaceDate(this.orderId, formatted).then(async (result) => {
                    // Dispatch custom event to notify parent component
                    this.$dispatch('order-updated', { orderId: this.orderId, result, isDateLoading: true });
                    // Dispatch custom event to update subscription dates
                    this.$dispatch('update-sub-dates', {orderId: this.orderId, date: formatted})
                    // Dispatch event to close modal
                    this.$dispatch('toast', { message: 'Date Updated!', type: 'success' });
                    this.$dispatch('close-modal');
                }).catch((error) => {
                    throw new Error('Failed to update order:', error)
                });
            }
        } catch (error) {
            throw new Error('Failed to update ship date:', error);
        }
      },

      updateDateEvent(event) {
            this.type = event.detail.type;
            this.orderId = event.detail.orderId;
            const newDate = event.detail.date;
            this.subscriptionId = event.detail.subscriptionId;
            this.selectedDate = toDateInputValue(newDate); // Use your helper function

            // 3. IMPORTANT: Tell the jsCalendar instance to update its UI
            if (this.calendar && event.detail.date) {
                this.calendar.set(new Date(newDate));
            }
        }
    }
}